import { Link, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Backdrop, CircularProgress } from '@mui/material'
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import GivebackLogo from 'assets/svg/giveback-logo.svg'
import { TextBody, AlertContainer } from 'components'
import { useAuthToken, useUnsavedChangesWarning } from 'hooks'
import { useLockedCardStatusUpdater } from 'hooks/useLockedCardStatusUpdater'
import { resetStore } from 'app/store'
import { clearAllUnmaskedCards, incrementImageRefreshCounter } from 'slices/customer'
import {
  HeaderContainer,
  HeaderRow,
  HeaderLogo,
  HeaderLogoutContainer,
  HeaderNavLinks,
  HeaderNavLinksRow,
  NavLinkLeftContainer,
  HeaderBannerWrapper
} from './Header.styled'

const NAV_ITEMS = [
  {
    path: '/',
    label: 'Home',
    icon: HomeOutlinedIcon
  },
  {
    path: '/customers/list',
    label: 'Customer',
    icon: PersonOutlineIcon
  },
  {
    path: '/profile',
    label: 'Profile',
    icon: PersonOutlineIcon
  }
]

const Header = ({ disableNavigation = false, verificationBanner = '' }) => {
  const location = useLocation()
  const dispatch = useDispatch()
  const hasUnsavedChanges = useSelector(state => state.user.hasUnsavedChanges)
  const { requestNavigation } = useUnsavedChangesWarning(hasUnsavedChanges)
  const { clearTokens } = useAuthToken()
  const checkAndUpdateStatusForLockedCards = useLockedCardStatusUpdater()
  const isStatusChangeLoading =
    checkAndUpdateStatusForLockedCards.isStatusChangeLoading

  const handleLogout = async () => {
    const didNavigate = requestNavigation('/login')
    if (didNavigate) {
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem('auth_access_customers_filter_applied')
      }

      clearTokens()
      resetStore()
    }
  }

  const handleNavClick = async path => {
    if (disableNavigation && path !== '/profile') {
      return
    }

    if (path === '/customers/list' && location.pathname !== '/customers/list') {
      await checkAndUpdateStatusForLockedCards(false)
    }

    const didNavigate = requestNavigation(path)
    if (didNavigate) {
      if (path === '/customers/list') {
        dispatch(clearAllUnmaskedCards())
        dispatch(incrementImageRefreshCounter())
      }
    }
  }

  const renderNavItem = ({ path, label, icon: Icon }) => (
    <HeaderNavLinksRow
      key={path}
      isActive={location.pathname === path}
      onClick={() => handleNavClick(path)}
      style={{
        cursor:
          disableNavigation && path !== '/profile' ? 'not-allowed' : 'pointer',
        opacity: disableNavigation && path !== '/profile' ? 0.5 : 1
      }}
    >
      <Icon />
      <TextBody>{label}</TextBody>
    </HeaderNavLinksRow>
  )

  return (
    <>
      <HeaderContainer>
        <HeaderRow>
          <HeaderLogo src={GivebackLogo} />
          <HeaderLogoutContainer onClick={handleLogout}>
            <ExitToAppOutlinedIcon />
            <TextBody cursor="pointer">Logout</TextBody>
          </HeaderLogoutContainer>
        </HeaderRow>
        {!!verificationBanner && (
          <HeaderBannerWrapper>
            <div style={{ pointerEvents: 'auto', marginTop: '8px' }}>
              <AlertContainer severity="error" margin="0 auto">
                {verificationBanner}
              </AlertContainer>
            </div>
          </HeaderBannerWrapper>
        )}
        <HeaderNavLinks>
          <NavLinkLeftContainer>
            {NAV_ITEMS.slice(0, 2).map(renderNavItem)}
          </NavLinkLeftContainer>
          {NAV_ITEMS.slice(2).map(renderNavItem)}
        </HeaderNavLinks>
      </HeaderContainer>

      {/* Loading backdrop for navigation */}
      <Backdrop
        open={isStatusChangeLoading && location.pathname !== '/customers/list'}
        sx={{
          color: '#fff',
          zIndex: theme => theme.zIndex.modal + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  )
}

export default Header
